import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rulesContainer", "ruleTemplate", "emptyState"]

  declare rulesContainerTarget: HTMLElement
  declare ruleTemplateTarget: HTMLTemplateElement
  declare emptyStateTarget: HTMLElement
  declare hasEmptyStateTarget: boolean

  connect() {
    this.updateRuleNumbers()
    this.updatePositions()
  }

  addRule(event: Event) {
    event.preventDefault()
    
    // Clone the template content
    const templateContent = this.ruleTemplateTarget.content.cloneNode(true) as DocumentFragment
    const newIndex = Date.now() // Use timestamp for unique index
    
    // Get the rule card from the cloned content
    const newRule = templateContent.firstElementChild as HTMLElement
    if (!newRule) return
    
    // Update all NEW_INDEX placeholders in the cloned content
    newRule.dataset.ruleIndex = String(newIndex)
    newRule.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[name*="NEW_INDEX"]').forEach(el => {
      el.name = el.name.replace(/NEW_INDEX/g, String(newIndex))
    })
    newRule.querySelectorAll<HTMLInputElement>('[value="NEW_INDEX"]').forEach(el => {
      el.value = String(newIndex)
    })
    
    this.rulesContainerTarget.appendChild(newRule)
    this.hideEmptyState()
    this.updateRuleNumbers()
    this.updatePositions()
  }

  removeRule(event: Event) {
    event.preventDefault()
    
    const target = event.target as HTMLElement
    const ruleCard = target.closest("[data-rule-index]") as HTMLElement
    if (!ruleCard) return
    
    // If persisted record, add _destroy field
    const idField = ruleCard.querySelector<HTMLInputElement>("input[name*='[id]']")
    if (idField && idField.value) {
      // Add _destroy field
      const destroyInput = document.createElement("input")
      destroyInput.type = "hidden"
      destroyInput.name = idField.name.replace("[id]", "[_destroy]")
      destroyInput.value = "1"
      ruleCard.appendChild(destroyInput)
      ruleCard.style.display = "none"
    } else {
      ruleCard.remove()
    }
    
    this.updateRuleNumbers()
    this.updatePositions()
    this.showEmptyStateIfNeeded()
  }

  moveRuleUp(event: Event) {
    event.preventDefault()
    const target = event.target as HTMLElement
    const ruleCard = target.closest("[data-rule-index]") as HTMLElement
    const prev = ruleCard?.previousElementSibling as HTMLElement
    if (prev && prev.hasAttribute("data-rule-index")) {
      ruleCard.parentNode?.insertBefore(ruleCard, prev)
      this.updateRuleNumbers()
      this.updatePositions()
    }
  }

  moveRuleDown(event: Event) {
    event.preventDefault()
    const target = event.target as HTMLElement
    const ruleCard = target.closest("[data-rule-index]") as HTMLElement
    const next = ruleCard?.nextElementSibling as HTMLElement
    if (next && next.hasAttribute("data-rule-index")) {
      ruleCard.parentNode?.insertBefore(next, ruleCard)
      this.updateRuleNumbers()
      this.updatePositions()
    }
  }

  addTarget(event: Event) {
    event.preventDefault()
    
    const target = event.target as HTMLElement
    const ruleCard = target.closest("[data-rule-index]") as HTMLElement
    if (!ruleCard) return
    
    const ruleIndex = ruleCard.dataset.ruleIndex
    const tbody = ruleCard.querySelector("[data-targets-body]")
    if (!tbody) return
    
    // Remove empty row if present
    const emptyRow = tbody.querySelector(".empty-row")
    if (emptyRow) emptyRow.remove()
    
    const targetIndex = Date.now()
    const baseName = `routing_policy[routing_rules_attributes][${ruleIndex}][routing_rule_targets_attributes][${targetIndex}]`
    
    // Count existing visible targets to determine position
    const existingTargets = tbody.querySelectorAll("tr:not([style*='display: none']):not(.empty-row)")
    const position = existingTargets.length

    const tr = document.createElement("tr")
    tr.dataset.targetIndex = String(targetIndex)
    tr.innerHTML = `
      <td>
        <input type="hidden" name="${baseName}[position]" value="${position}" />
        <select name="${baseName}[provider_slug]" class="select select-bordered select-xs w-full">
          <option value="">Select...</option>
          ${this.getProviderOptions()}
        </select>
      </td>
      <td>
        <select name="${baseName}[model_slug]" class="select select-bordered select-xs w-full">
          <option value="">Select...</option>
          ${this.getModelOptions()}
        </select>
      </td>
      <td>
        <input type="number" name="${baseName}[priority]" value="0" class="input input-bordered input-xs w-full" min="0" />
      </td>
      <td>
        <input type="number" name="${baseName}[weight]" value="100" class="input input-bordered input-xs w-full" min="1" max="100" />
      </td>
      <td>
        <button type="button" class="btn btn-ghost btn-xs btn-square text-error" data-action="routing-policy-form#removeTarget">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </td>
    `
    tbody.appendChild(tr)
  }

  removeTarget(event: Event) {
    event.preventDefault()
    
    const target = event.target as HTMLElement
    const tr = target.closest("tr")
    if (!tr) return
    
    const idField = tr.querySelector<HTMLInputElement>("input[name*='[id]']")
    if (idField && idField.value) {
      // Add _destroy field for persisted records
      const destroyInput = document.createElement("input")
      destroyInput.type = "hidden"
      destroyInput.name = idField.name.replace("[id]", "[_destroy]")
      destroyInput.value = "1"
      tr.appendChild(destroyInput)
      tr.style.display = "none"
    } else {
      tr.remove()
    }
  }

  updateRuleNumbers() {
    const rules = this.rulesContainerTarget.querySelectorAll<HTMLElement>("[data-rule-index]:not([style*='display: none'])")
    rules.forEach((rule, index) => {
      const numberSpan = rule.querySelector("[data-rule-number]")
      if (numberSpan) {
        numberSpan.textContent = String(index + 1)
      }
      // Update badge
      const badge = rule.querySelector(".badge")
      if (badge && badge.textContent?.includes("Rule #")) {
        badge.textContent = `Rule #${index + 1}`
      }
    })
  }

  updatePositions() {
    const rules = this.rulesContainerTarget.querySelectorAll<HTMLElement>("[data-rule-index]:not([style*='display: none'])")
    rules.forEach((rule, index) => {
      const positionField = rule.querySelector<HTMLInputElement>("[data-position-field]")
      if (positionField) {
        positionField.value = String(index)
      }
    })
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  showEmptyStateIfNeeded() {
    const visibleRules = this.rulesContainerTarget.querySelectorAll("[data-rule-index]:not([style*='display: none'])")
    if (visibleRules.length === 0 && this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  getProviderOptions(): string {
    // Get options from existing select in the template
    const select = this.ruleTemplateTarget.content.querySelector<HTMLSelectElement>("select[name*='provider_slug']")
    if (select) {
      return Array.from(select.options).map(opt => 
        `<option value="${opt.value}">${opt.textContent}</option>`
      ).join("")
    }
    return ""
  }

  getModelOptions(): string {
    const select = this.ruleTemplateTarget.content.querySelector<HTMLSelectElement>("select[name*='model_slug']")
    if (select) {
      return Array.from(select.options).map(opt => 
        `<option value="${opt.value}">${opt.textContent}</option>`
      ).join("")
    }
    return ""
  }
}

